import dayjs, { Dayjs } from 'dayjs'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card } from '@mui/material'
import { message } from 'antd'
import * as XLSX from 'xlsx'
import PageUrls from '../../../../constants/page-urls'
import useAxios from '../../../../hooks/useAxios'
import { IMerchant } from '../../../../types/merchant'
import { IFilterMapped } from './components/DailySalesReportFilter'
import DailyTelesalesReportTable from './components/DailyTelesalesReportTable'
import DailyTelesalesReportFilter from './components/DailyTelesalesReportFilter'

const Menus: any[] = []

export interface IMerchantDailyReport {
  merchant: string
  date: string
  count: number
  unit: number
  discount: number
  subTotal: number
  total: number
  commission: number
  commissionOrder: number
  commissionPercent: number
  codTotal: number
  codCount: number
  bankTotal: number
  bankCount: number
  hasTracking: number
  noTracking: number
  costTotal: number
}

export interface ICostDailyReport {
  merchant: string
  amount: number
  date: string
}

const DailyTelesalesReport = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  // const initFilters =
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    status: queryStr.status?.length ? String(queryStr.status) : undefined,
    shippingCostDisplay: queryStr.shippingCostDisplay?.length
      ? String(queryStr.shippingCostDisplay)
      : undefined,
    calTax: queryStr.calTax?.length ? String(queryStr.calTax) : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    trackingStatus: queryStr.trackingStatus
      ? String(queryStr.trackingStatus)
      : undefined,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    status: queryStr.status?.length ? String(queryStr.status) : undefined,
    shippingCostDisplay: queryStr.shippingCostDisplay?.length
      ? String(queryStr.shippingCostDisplay)
      : undefined,
    calTax: queryStr.calTax?.length ? String(queryStr.calTax) : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    trackingStatus: queryStr.trackingStatus
      ? String(queryStr.trackingStatus)
      : undefined,
  })

  const tableRef = useRef(null)

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [filter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Report.DailySales}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const merchantDailyReports = useAxios<any>({
    method: 'POST',
    url: '/report/order-telesale-reports',
    data: {
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      products: searchFilter.productObjectIds,
      productGroups: searchFilter.productGroups,
      categories: searchFilter.categoryObjectIds,
      distribution: searchFilter.distributionObjectIds,
      status: searchFilter.status !== 'all' ? searchFilter.status : undefined,
      shippingCostDisplay:
        searchFilter.shippingCostDisplay !== 'all'
          ? searchFilter.shippingCostDisplay
          : undefined,
      calTax: searchFilter.calTax !== 'all' ? searchFilter.calTax : undefined,
      trackingStatus: searchFilter.trackingStatus,
    },
  })

  const costDailyReport = useAxios<[ICostDailyReport]>({
    method: 'GET',
    url: '/report/cost-daily-reports',
    params: {
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      distribution: searchFilter.distributionObjectIds,
      products: searchFilter.productObjectIds,
      categories: searchFilter.categoryObjectIds,
    },
  })

  const merchant = useAxios<IMerchant>({
    method: 'GET',
    url: '/merchant/me',
  })

  const handleOnDownload = useCallback(async (date: Dayjs) => {
    if (tableRef.current) {
      const ws = XLSX.utils.table_to_book(tableRef.current)
      const excelBuffer = XLSX.write(ws, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' })

      const url = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = `dailyReport.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
      message.success('ดาวน์โหลดสำเร็จ')
    }
  }, [])

  return (
    <Card
      sx={{
        padding: 2,
      }}
    >
      <DailyTelesalesReportFilter
        filter={filter}
        onFilterChange={onFilterChange}
        onSearch={handleSearch}
        onDownload={handleOnDownload}
      />
      <DailyTelesalesReportTable
        filter={filter}
        isLoading={merchantDailyReports.isLoading}
        merchantDailyReports={merchantDailyReports?.data?.telesale ?? []}
        merchant={merchant.data}
        // costDailyReport={costDailyReport.data ?? []}
        ref={tableRef}
      />
    </Card>
  )
}

export default DailyTelesalesReport
